import React, { useRef, useState } from 'react';
import axios from 'axios';
import { Upload, X, Loader, Image as ImageIcon } from 'lucide-react';
import { toast } from 'sonner';

const API = '/api';
function getHeaders() {
  const t = localStorage.getItem('pkepl_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
}

/**
 * ImageUpload — lightweight upload field for admin forms.
 * Props:
 *   value       — current file_id (string) or null
 *   previewUrl  — full URL to display preview (string) or null
 *   onChange    — (fileId, url) => void  called after successful upload
 *   onClear     — () => void             called when image is removed
 *   label       — string label for the upload field
 *   hint        — string hint text (optional)
 *   accept      — MIME accept string (default: image/*)
 */
export default function ImageUpload({ value, previewUrl, onChange, onClear, label = 'Upload Image', hint = 'JPG, PNG, SVG or WEBP · Max 5MB', accept = 'image/*' }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const doUpload = async (file) => {
    if (!file) return;
    const ext = file.name.split('.').pop().toLowerCase();
    if (!['jpg', 'jpeg', 'png', 'webp', 'svg'].includes(ext)) {
      toast.error('File type not supported. Use JPG, PNG, SVG or WEBP.');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File exceeds 5MB limit.');
      return;
    }
    setUploading(true);
    const form = new FormData();
    form.append('file', file);
    try {
      const { data } = await axios.post(`${API}/admin/upload`, form, {
        withCredentials: true,
        headers: { ...getHeaders(), 'Content-Type': 'multipart/form-data' },
      });
      onChange(data.id, `${data.url}`);
      toast.success('Image uploaded');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) doUpload(file);
  };

  return (
    <div className="space-y-2" data-testid={`image-upload-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <label className="block text-xs font-semibold text-[#374151] font-sans uppercase tracking-wide">{label}</label>

      {previewUrl ? (
        <div className="relative inline-block group">
          <img
            src={previewUrl}
            alt={label}
            className="h-20 w-auto max-w-[200px] object-contain border border-[#E5E7EB] rounded-lg bg-[#F7FAFC] p-2"
          />
          <button
            onClick={onClear}
            className="absolute -top-2 -right-2 bg-white border border-[#E5E7EB] rounded-full p-0.5 shadow-sm hover:bg-red-50 hover:border-red-200 transition-colors"
            title="Remove image"
          >
            <X className="w-3.5 h-3.5 text-[#6B7280] hover:text-red-500" />
          </button>
          <button
            onClick={() => inputRef.current?.click()}
            className="mt-2 block text-xs text-[#0E5A57] hover:underline font-sans"
          >
            Replace
          </button>
        </div>
      ) : (
        <div
          onClick={() => inputRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          className={`flex flex-col items-center justify-center gap-2 border-2 border-dashed rounded-xl px-6 py-6 cursor-pointer transition-colors duration-150 ${dragOver ? 'border-[#0E5A57] bg-teal-50' : 'border-[#D1D5DB] hover:border-[#0E5A57] hover:bg-teal-50/40'}`}
        >
          {uploading ? (
            <Loader className="w-5 h-5 text-[#0E5A57] animate-spin" />
          ) : (
            <>
              <div className="w-9 h-9 bg-[#F3F4F6] rounded-lg flex items-center justify-center">
                <ImageIcon className="w-4 h-4 text-[#9CA3AF]" />
              </div>
              <p className="text-sm text-[#374151] font-sans">
                <span className="font-medium text-[#0E5A57]">Click to upload</span> or drag & drop
              </p>
            </>
          )}
        </div>
      )}

      {!previewUrl && uploading && (
        <div className="flex items-center gap-2 text-xs text-[#6B7280] font-sans">
          <Loader className="w-3 h-3 animate-spin" /> Uploading...
        </div>
      )}

      <p className="text-xs text-[#9CA3AF] font-sans">{hint}</p>
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        className="hidden"
        onChange={(e) => doUpload(e.target.files?.[0])}
      />
    </div>
  );
}
